import type { DocumentKind } from '../../api/types/documents';

/**
 * Per-product switchboard for the shared `ReservationDetailPage`. Replaces the original
 * Air-only boolean props (`allowVoidCancel` / `airResumePayment` / `showAirFlightDetails`)
 * with one object so every product module can opt into exactly the post-sale operations,
 * documents and detail panels that make sense for it.
 *
 * Scope note (2026-08-27): the destructive `ShoppingCart/{RefundCancel,Open,Change,Edit,
 * AddNote}` operations were pulled from the UI — live testing showed Open/Change/Edit are
 * backend skeletons that mutate unconditionally.
 *
 * Scope note (2026-09-08): the disabled placeholders for those five `ShoppingCart/*`
 * operations ("Bilet İşlemleri" card / `PostSaleSkeletonSection`) and the automated GDS
 * `Air/Modify/{Refund,Exchange}` buttons on `TicketPostSaleActions` were removed — the
 * backend routes throw / mutate unconditionally (docs/backend-requirements.md §N-1 / §F-12)
 * with no sign of landing, so a permanently-disabled control was just noise. The API
 * wrappers (api/endpoints/postSale.ts) stay for when the backend delivers. `Air/RePrice`
 * (`reprice` below) is genuinely live; the live "İade" is `ShoppingCart/Refund`.
 *
 * Scope note (2026-08-28): the detail page splits by record state (see
 * `reservationRecordKind` / `capabilitiesForKind` below). A still-`Prebooked` Air
 * reservation keeps **İptal Et** (`Air/CancelReservation`) + **Opsiyon/Fiyat**
 * (`ShoppingCart/EditOptionAndPrice`) + Ödemeye Git; an issued Air ticket
 * (`Booked`/`Void`/`Refund`) instead gets **Void** (`/Void`) + **İade**
 * (`ShoppingCart/Refund`) and never shows İptal Et.
 *
 * Scope note (2026-08-31): `cancel` is now a product tag, not a boolean — Hotel and
 * RentCar have their own cancel endpoints (`Hotel/GetReservationCancel`,
 * `RentCar/CancelOrder`). Those bookings have no "ticket" concept, so their İptal Et
 * stays available regardless of record status (see `capabilitiesForKind`). Bus/Transfer
 * providers do not offer cancellation — no `cancel` for them.
 */
export type CancelKind = 'air' | 'hotel' | 'rentcar';

export interface ReservationCapabilities {
  /** Which product's cancel endpoint İptal Et calls. `air` → `Air/CancelReservation`
   *  (Prebooked only); `hotel` → `Hotel/GetReservationCancel`; `rentcar` →
   *  `RentCar/CancelOrder`. Omitted → no İptal Et. */
  cancel?: CancelKind;
  /** ShoppingCart/EditOptionAndPrice (offered reservations — option expiry + price). */
  editOptionAndPrice?: boolean;
  /** `Air/RePrice` — re-price a still-held (`Prebooked`) reservation against the current
   *  fare and show the returned `ShoppingCart` totals. Reservation state only; an issued
   *  ticket is re-priced through an exchange, not here. */
  reprice?: boolean;
  /** Generic `/Void` — void an issued ticket (within the airline's void window). Ticket only. */
  void?: boolean;
  /** `ShoppingCart/Refund` — refund an issued ticket (business-rule enforced). Ticket only. */
  refund?: boolean;
  /** Jump back into the booking flow's payment step for an unpaid reservation. */
  resumePayment?: boolean;
  /** Rich flight segment / fare-breakdown panel (reads Air-specific shapes). */
  flightDetails?: boolean;
  /** Which documents the "Belgeler" menu offers. Empty / omitted → no menu. */
  documents?: DocumentKind[];
}

/** True when the reservation supports the one extended `ShoppingCart/*` operation the
 *  shared `PostSaleActions` toolbar still renders (option/price — cancel is separate). */
export function hasPostSaleActions(caps: ReservationCapabilities): boolean {
  return !!caps.editOptionAndPrice;
}

/** Which detail-page treatment a record gets. An Air record stays a *reservation*
 *  (cancellable, re-optionable) only while every pax row is still `Prebooked`; once any
 *  ticket is issued it's a *ticket* — İptal Et / Opsiyon-Fiyat no longer apply, Void /
 *  İade do. */
export type ReservationRecordKind = 'reservation' | 'ticket';

export function reservationRecordKind(statuses: Array<string | null | undefined>): ReservationRecordKind {
  const known = statuses
    .map((s) => (s ?? '').replace(/İ/g, 'i').trim().toLowerCase())
    .filter(Boolean);
  if (known.length === 0) return 'reservation';
  return known.every((s) => s === 'prebooked' || s === 'stagedbooking') ? 'reservation' : 'ticket';
}

/** Narrows the static per-product caps down to what's valid for a record in this state.
 *  The reservation/ticket split is an Air concept (an issued air ticket is voided/refunded,
 *  not "cancelled"); Hotel/RentCar have no ticket concept, so their `cancel` survives into
 *  the `ticket` kind. */
export function capabilitiesForKind(
  caps: ReservationCapabilities,
  kind: ReservationRecordKind
): ReservationCapabilities {
  if (kind === 'ticket') {
    // Strip cancel only on the Air path (caps that also carry void/refund).
    const cancel = caps.void || caps.refund ? undefined : caps.cancel;
    return { ...caps, cancel, editOptionAndPrice: false, resumePayment: false, reprice: false };
  }
  return { ...caps, void: false, refund: false };
}

export const AIR_CAPS: ReservationCapabilities = {
  cancel: 'air',
  editOptionAndPrice: true,
  reprice: true,
  void: true,
  refund: true,
  resumePayment: true,
  flightDetails: true,
  // `offer` (Teklif/Proforma) re-added (SA-2): `Document/Offer` needs an `offerId`, now
  // fed by the "Teklif Oluştur" button on the search results (`AirResultsList` — from
  // `AirSearchResponse.offerId`). On the reservation-detail page there's no search
  // `offerId`, so `DocumentMenu` shows a friendly "teklif kimliği gerekli" warning
  // instead of calling the endpoint (see DocumentMenu.fetchDocument).
  documents: ['eticket', 'eticket-grouped', 'offer'],
};

// Hotel / RentCar: their own cancel endpoint + document menu (Hotel voucher).
export const HOTEL_CAPS: ReservationCapabilities = {
  cancel: 'hotel',
  documents: ['voucher'],
};

export const RENTCAR_CAPS: ReservationCapabilities = {
  cancel: 'rentcar',
};

// Bus / Transfer: providers offer no cancellation — info + documents only.
export const BUS_CAPS: ReservationCapabilities = {};

export const TRANSFER_CAPS: ReservationCapabilities = {};
